/**
 * @lodex-pro/web-portal/nav/PortalShell — layout + plan-aware sidebar.
 *
 * Phase 15 / Task 15.4 (DEC-153) — sidebar with nav entries. Entries the operator's plan
 * does not include show a lock icon + `aria-disabled` and do NOT navigate on click.
 * OPP PR 6: the plan is the operator's features (portal-features.ts), the same ones the
 * API gates on; with OP_PORTAL_LOGIN off the portal says so instead of showing anything.
 * OPP PR 13a: custom reports and webhooks, placeholders that still locked by tier, were removed
 * until they ship as real plan features; the tier badge went with operator_tier.
 * OPP PR 7: "Tank setup" and "Routes" (OPERATOR_ADMIN, FOREMAN; a foreman reads Tank setup only,
 * tank registry PR 1) appear only for those roles, locked like any other entry when
 * OP_ROUTES_PUMPERS is off.
 * OPP PR 12a: "Request a haul" and "My requests" appear for the SUBMIT_HAUL_REQUEST roles,
 * locked when OP_HAUL_REQUESTS or OP_ROUTES_PUMPERS is off.
 * Sign-out lives in the header. Uses `usePortalMe()` to read the company name and its
 * plan features (single source of truth). `<Outlet />` renders the active route.
 */

import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useViewAs } from '../auth/view-as';
import { ViewAsBanner } from './ViewAsBanner';
import { usePortalMe } from '../hooks/useOperatorPortal';
import { portalOrgName } from './org-name';
import {
  NOT_ON_PLAN,
  PORTAL_OFF_MESSAGE,
  portalHaulRequestsOnPlan,
  portalIsAdmin,
  portalLacks,
  portalManagesRoutes,
  portalRequestsHauls,
} from './portal-features';

interface NavEntryProps {
  readonly to: string;
  readonly label: string;
  readonly allowed: boolean;
  /** Shown on a locked entry. */
  readonly lockedTitle?: string;
}

function NavEntry({
  to,
  label,
  allowed,
  lockedTitle = NOT_ON_PLAN,
}: NavEntryProps): React.JSX.Element {
  if (!allowed) {
    return (
      <div
        role="link"
        aria-disabled="true"
        title={lockedTitle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          color: 'var(--color-text-tertiary)',
          cursor: 'not-allowed',
          fontSize: '14px',
        }}
      >
        <span aria-label={`Locked: ${lockedTitle}`}>🔒</span>
        {label}
      </div>
    );
  }
  return (
    <NavLink
      to={to}
      end
      style={({ isActive }): React.CSSProperties => ({
        display: 'block',
        padding: '8px 12px',
        color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
        background: isActive ? 'var(--color-bg-tertiary)' : 'transparent',
        fontWeight: isActive ? 600 : 400,
        textDecoration: 'none',
        borderRadius: '12px',
        fontSize: '14px',
      })}
    >
      {label}
    </NavLink>
  );
}

export function PortalShell(): React.JSX.Element {
  const { data } = usePortalMe();
  // 0202: Paelode's display name when linked to a platform operator.
  const tenantName = portalOrgName(data);
  const switchedOff = portalLacks(data, 'OP_PORTAL_LOGIN');
  // OPP PR 7 — tank setup is the admin's; routes are the admin's and the foremen's. Pumpers
  // see neither entry. Both sit under OP_ROUTES_PUMPERS and lock like any plan feature.
  const routesOnPlan = !portalLacks(data, 'OP_ROUTES_PUMPERS');
  // OPP PR 12a — haul requests sit under OP_HAUL_REQUESTS and OP_ROUTES_PUMPERS.
  const haulRequestsOnPlan = portalHaulRequestsOnPlan(data);
  const navigate = useNavigate();
  // PR 5 — a Paelode admin viewing as this company: the banner's End replaces Sign out.
  const { session: viewAs } = useViewAs();
  const queryClient = useQueryClient();

  const handleSignOut = (): void => {
    void (async (): Promise<void> => {
      try {
        await signOut({ global: false });
      } catch {
        // Leave the portal either way, as before; the cache is cleared below regardless.
      } finally {
        // OPEN-182 — the next user in this tab must never see this company's cached data.
        // QueryProvider also clears on Amplify's signedOut; this covers a sign-out that throws.
        queryClient.clear();
        void navigate('/', { replace: true });
      }
    })();
  };

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
      }}
    >
      <aside
        aria-label="Portal navigation"
        style={{
          width: '220px',
          padding: '16px 12px',
          borderRight: '1px solid var(--color-border-primary)',
          background: 'var(--color-bg-rail)',
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: '16px', fontSize: '15px' }}>lodeX Pro</div>
        {!switchedOff && (
          <nav
            aria-label="Primary"
            style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}
          >
            <NavEntry to="/portal" label="Home" allowed />
            <NavEntry
              to="/portal/tickets"
              label="Tickets"
              allowed={!portalLacks(data, 'OP_TICKETS')}
            />
            <NavEntry
              to="/portal/volumes"
              label="Volumes"
              allowed={!portalLacks(data, 'OP_VOLUMES')}
            />
            {/* Operator Reports (c), 0256 — the statement (the CSV / Excel export's gate). */}
            <NavEntry
              to="/portal/reports"
              label="Reports"
              allowed={!portalLacks(data, 'OP_TICKETS') && !portalLacks(data, 'OP_REPORTS')}
            />
            <NavEntry
              to="/portal/tanks"
              label="Tanks"
              allowed={!portalLacks(data, 'OP_TANK_LEVELS')}
            />
            {/* Tank registry PR 1 (0245) — foremen read the company's tanks and batteries too. */}
            {portalManagesRoutes(data) && (
              <NavEntry to="/portal/tank-setup" label="Tank setup" allowed={routesOnPlan} />
            )}
            {portalManagesRoutes(data) && (
              <NavEntry to="/portal/routes" label="Routes" allowed={routesOnPlan} />
            )}
            {/* OPP PR 8 — every operator role; the admin picks tanks for others ("Contacts"). */}
            <NavEntry
              to="/portal/my-tanks"
              label={portalIsAdmin(data) ? 'Contacts' : 'My tanks'}
              allowed={routesOnPlan}
            />
            {/* OPP PR 12a — turning a load in (SUBMIT_HAUL_REQUEST: every operator role). */}
            {portalRequestsHauls(data) && (
              <>
                <NavEntry
                  to="/portal/request-haul"
                  label="Request a haul"
                  allowed={haulRequestsOnPlan}
                />
                <NavEntry
                  to="/portal/my-requests"
                  label="My requests"
                  allowed={haulRequestsOnPlan}
                />
              </>
            )}
            <NavEntry
              to="/portal/api-keys"
              label="API usage"
              allowed={!portalLacks(data, 'OP_API')}
            />
            {/* OPP PR 9 — the portal admin manages the company's API keys. Not plan-locked:
                without OP_API the page still lists and revokes, and says why it can't issue. */}
            {portalIsAdmin(data) && (
              <NavEntry to="/portal/api-keys/manage" label="API keys" allowed />
            )}
            {/* Operator webhooks PR 1 (0239) — the portal admin's webhook endpoints, on a plan with
                OP_WEBHOOKS. */}
            {portalIsAdmin(data) && (
              <NavEntry
                to="/portal/webhooks"
                label="Webhooks"
                allowed={!portalLacks(data, 'OP_WEBHOOKS')}
              />
            )}
            <NavEntry to="/portal/settings" label="Settings" allowed />
          </nav>
        )}
      </aside>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {viewAs !== null && <ViewAsBanner session={viewAs} />}
        <header
          aria-label="Portal header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 24px',
            borderBottom: '1px solid var(--color-border-primary)',
            background: 'var(--color-bg-rail)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '14px', color: 'var(--color-text-tertiary)' }}>
              {tenantName}
            </span>
          </div>
          {viewAs === null && (
            <button
              type="button"
              onClick={handleSignOut}
              aria-label="Sign out"
              style={{
                padding: '6px 14px',
                minHeight: '32px',
                cursor: 'pointer',
                background: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: 'var(--radius-pill)',
                fontSize: '13px',
              }}
            >
              Sign out
            </button>
          )}
        </header>
        <div style={{ flex: 1 }}>
          {switchedOff ? (
            <main style={{ padding: '32px', maxWidth: '640px', margin: '0 auto' }}>
              <p role="status" style={{ fontSize: '16px' }}>
                {PORTAL_OFF_MESSAGE}
              </p>
            </main>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  );
}
